/**
 * The three-way verdict on a THROWN transport error, in the shared `HTTP_CLIENT_ERROR_CODES` vocabulary: TIMEOUT for an
 * abort, NETWORK_ERROR for the `TypeError` a failed fetch raises, UNKNOWN_ERROR for anything else.
 *
 * ONE decision, several diallers. `httpFetch` (`tools/http/fetch.ts`) closes over the GLOBAL fetch, and the app tier's
 * service-binding flow client dials a Fetcher binding it cannot reach; both need the same classification, and a caller
 * whose timeout came back UNKNOWN_ERROR cannot be retried on the timeout policy.
 *
 * `getErrorMessage` arrives as a DEP rather than an import: a `utils/` leaf may value-import only `@federa/const` and
 * `@federa/types` (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`), so the composition root that owns the dialler binds it.
 * `isAbortError` is const-homed (`const/http-error-codes.ts`) and imported directly.
 *
 * Returns the code plus its message and NO `ok` tag: each consumer owns its own envelope shape and spreads this in.
 */

import { ERROR_MESSAGES } from '@federa/const/error-messages';
import { HTTP_CLIENT_ERROR_CODES, isAbortError, type HttpClientErrorCode } from '@federa/const/http-error-codes';
import { ERROR_REQUEST_TIMED_OUT } from '@federa/const/http-error-values';

/** The error reader this leaf cannot import (`utils/` leaves do not reach each other); it lives in `utils/errors/error-core.ts`. */
export interface TransportFailureClassifyDeps {
	getErrorMessage: (error: unknown) => string;
}

/**
 * THE NETWORK_ERROR ARM ON ITS OWN, because a THIRD dialler makes the same call and cannot take the whole classifier.
 * `handleCaughtError` (`components/lib/api/fetch-helpers.ts`) splits the abort arm into cancelled-vs-timeout and answers
 * every arm as an `AdminAPIError` carrying its own status, so it can adopt this decision and nothing else -- and it had
 * re-typed the predicate by hand. Two spellings of one classification is how a network outage starts reading as
 * UNKNOWN_ERROR on one dialler (which the retry policy will not retry) and NETWORK_ERROR on the other, for the identical
 * throw, with nothing typed on either side.
 *
 * WHAT THE SUBSTRING IS, since it looks arbitrary: `fetch` is the lowest common denominator of the message a failed
 * fetch raises across the runtimes this tree dials from -- `fetch failed` (workerd/undici), `Failed to fetch`
 * (Chromium), `NetworkError when attempting to fetch resource` (Gecko). The `TypeError` narrowing is what keeps it
 * honest: this is a PLATFORM error carrying no code to read, which is exactly the shape
 * `QUAL-ERROR-MESSAGE-SUBSTRING-CLASSIFY-001` exempts, and the reason the cure here is one home rather than a code.
 */
export function isFetchNetworkError(error: unknown): error is TypeError {
	return error instanceof TypeError && error.message.includes('fetch');
}

/** Classify `error` into `{ code, error }`; the caller wraps it in whatever failure envelope it answers. */
export function classifyTransportFailure(error: unknown, deps: TransportFailureClassifyDeps): { code: HttpClientErrorCode; error: string } {
	if (isAbortError(error)) {
		return { code: HTTP_CLIENT_ERROR_CODES.TIMEOUT, error: ERROR_REQUEST_TIMED_OUT };
	}
	if (isFetchNetworkError(error)) {
		return { code: HTTP_CLIENT_ERROR_CODES.NETWORK_ERROR, error: ERROR_MESSAGES.NETWORK_ERROR };
	}
	return { code: HTTP_CLIENT_ERROR_CODES.UNKNOWN_ERROR, error: deps.getErrorMessage(error) };
}
